/**
 * Copies content to clipboard supporting both HTML and plain text formats
 * @param htmlContent - The HTML content to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const copyToClipboard = async (
  htmlContent: string,
): Promise<boolean> => {
  try {
    // Create a temporary element for safe HTML parsing
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";

    // Modern clipboard API with dual format support
    if (navigator.clipboard?.write) {
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob,
        }),
      ]);
      return true;
    }

    // Fallback to plain text only
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(plainText);
      return true;
    }

    // No clipboard API available
    console.warn("Clipboard API not available, trying fallback");
    const range = document.createRange();
    range.selectNode(tempDiv);
    const selection = window.getSelection();

    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
      const successful = document.execCommand("copy");
      selection.removeAllRanges();
      return successful;
    }
    return false;
  } catch (error) {
    console.error("Failed to copy content:", error);
    return false;
  }
};

/**
 * Copies plain text to the clipboard.
 * @param text - Plain text to copy
 * @returns True if the copy succeeded
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // No clipboard API available
    console.warn("Clipboard API not available");
    return false;
  } catch (error) {
    console.error("Failed to copy text:", error);
    return false;
  }
};
